/**
 * RKHPage Component
 * Main page for RKH (Rencana Kerja Harian) with list view, filtering, and detail view
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Flex,
    VStack,
    Text,
    Spinner,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    HStack,
    IconButton,
    useToast,
    useBreakpointValue,
    Card,
    CardBody,
    Stack,
    Heading,
    Divider,
} from '@chakra-ui/react';
import { FiEye, FiChevronLeft, FiChevronRight, FiEdit } from 'react-icons/fi';
import Navbar from '../../../components/Navbar';
import FilterRKH from '../components/FilterRKH';
import RKHDetailModal from '../components/RKHDetailModal';
import RKHLocationModal from '../components/RKHLocationModal';
import { useRKH } from '../hooks/useRKH';
import { useRKHDetail } from '../hooks/useRKHDetail';
import { useAuth } from '../../../auth/AuthContext';
import { RKHFilters, RKH_STAGE_LABELS, RKH_STAGE_COLORS } from '../types';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';

const RKHPage: React.FC = () => {
    const toast = useToast();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const { user } = useAuth();

    const { data, loading, error, pagination, fetchData, setPage } = useRKH();
    const {
        data: detailData,
        loading: detailLoading,
        error: detailError,
        fetchDetail,
        clearDetail
    } = useRKHDetail();

    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [selectedRKH, setSelectedRKH] = useState<{ id: string; number: string } | null>(null);
    const [hasAppliedFilters, setHasAppliedFilters] = useState(true);

    // Get default date range (Today)
    const getDefaultDateRange = () => {
        const today = new Date();

        return {
            date_from: today.toISOString().split('T')[0],
            date_to: today.toISOString().split('T')[0],
        };
    };

    const [filters, setFilters] = useState<RKHFilters>({
        ...getDefaultDateRange(),
        estate_id: null,
        division_id: null,
        stage: null,
        page: 1,
        limit: 20,
    });

    const formatDate = (dateStr: string) => {
        try {
            return format(new Date(dateStr), 'dd/MM/yyyy', { locale: id });
        } catch {
            return dateStr;
        }
    };

    const handleFilterChange = useCallback((newFilters: RKHFilters) => {
        setFilters(newFilters);
    }, []);

    // Initial fetch on mount
    useEffect(() => {
        fetchData({ ...filters, page: 1 });
    }, []);

    const handleApplyFilters = useCallback(() => {
        setHasAppliedFilters(true);
        fetchData({ ...filters, page: 1 });
    }, [filters, fetchData]);

    const handleClearFilters = useCallback(() => {
        setHasAppliedFilters(false);
    }, []);

    const handleViewDetail = useCallback(async (rkhId: string) => {
        setIsDetailModalOpen(true);
        await fetchDetail(rkhId);
    }, [fetchDetail]);

    const handleCloseDetailModal = useCallback(() => {
        setIsDetailModalOpen(false);
        clearDetail();
    }, [clearDetail]);

    const handleEditLocation = useCallback((rkhId: string, rkhNumber: string) => {
        setSelectedRKH({ id: rkhId, number: rkhNumber });
        setIsLocationModalOpen(true);
    }, []);

    const handleCloseLocationModal = useCallback(() => {
        setIsLocationModalOpen(false);
        setSelectedRKH(null);
    }, []);

    const handleLocationUpdateSuccess = useCallback(() => {
        // Refresh the RKH list after successful location update
        fetchData(filters);
        toast({
            title: 'Berhasil',
            description: 'Lokasi RKH berhasil diperbarui',
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
    }, [fetchData, filters, toast]);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [setPage]);

    // Show error toast
    useEffect(() => {
        if (error && hasAppliedFilters) {
            toast({
                title: 'Error',
                description: error,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    }, [error, hasAppliedFilters, toast]);

    return (
        <Box minH="100vh" bg="gray.50">
            <Navbar />
            <Flex
                h={{ base: 'auto', md: 'calc(100vh - 40px)' }}
                direction={{ base: 'column', md: 'row' }}
            >
                {/* Mobile Header */}
                <Box display={{ base: 'block', md: 'none' }} p={4} pb={0}>
                    <Heading size="lg" color="gray.700">
                        Rencana Kerja Harian (RKH)
                    </Heading>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                        Ditemukan {pagination?.total_items || 0} data RKH
                    </Text>
                </Box>

                {/* Sidebar Filter - Desktop */}
                <Box
                    w={{ base: '100%', md: '320px' }}
                    bg={{ base: 'transparent', md: 'white' }}
                    borderRight={{ base: 'none', md: '1px solid' }}
                    borderBottom={{ base: 'none', md: 'none' }}
                    borderColor="gray.200"
                    overflow="hidden"
                    maxH={{ base: 'none', md: 'calc(100vh - 40px)' }}
                    overflowY={{ base: 'visible', md: 'auto' }}
                    p={{ base: 4, md: 0 }}
                >
                    <FilterRKH
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onClearFilters={handleClearFilters}
                        onApplyFilters={handleApplyFilters}
                    />
                </Box>

                {/* Main Content */}
                <Box flex="1" overflowY={{ base: 'visible', md: 'auto' }} p={{ base: 4, md: 6 }} pt={{ base: 0, md: 6 }}>
                    {!hasAppliedFilters ? (
                        <VStack spacing={4} align="center" justify="center" h="100%">
                            <Text fontSize="lg" color="gray.500" textAlign="center">
                                Pilih filter dan klik "Terapkan" untuk melihat data RKH
                            </Text>
                        </VStack>
                    ) : loading ? (
                        <VStack spacing={4} align="center" justify="center" h="100%">
                            <Spinner size="xl" color="blue.500" />
                            <Text fontSize="lg" color="gray.500">
                                Memuat data...
                            </Text>
                        </VStack>
                    ) : error ? (
                        <VStack spacing={4} align="center" justify="center" h="100%">
                            <Text fontSize="lg" color="red.500" textAlign="center">
                                {error}
                            </Text>
                            <Button colorScheme="blue" onClick={handleApplyFilters}>
                                Coba Lagi
                            </Button>
                        </VStack>
                    ) : data.length === 0 ? (
                        <VStack spacing={4} align="center" justify="center" h="100%">
                            <Text fontSize="lg" color="gray.500">
                                Tidak ada data RKH ditemukan
                            </Text>
                        </VStack>
                    ) : (
                        <VStack spacing={6} align="stretch">
                            {/* Header */}
                            <Flex
                                justify="space-between"
                                align="center"
                                flexWrap="wrap"
                                gap={4}
                                display={{ base: 'none', md: 'flex' }}
                            >
                                <Box>
                                    <Heading size="lg" color="gray.700">
                                        Rencana Kerja Harian (RKH)
                                    </Heading>
                                    <Text fontSize="sm" color="gray.600" mt={1}>
                                        Ditemukan {pagination?.total_items || 0} data RKH
                                    </Text>
                                </Box>
                            </Flex>

                            {/* Desktop Table View */}
                            {!isMobile ? (
                                <Box bg="white" borderRadius="lg" overflow="hidden" boxShadow="sm">
                                    <Table variant="simple">
                                        <Thead bg="gray.100">
                                            <Tr>
                                                <Th textAlign="center">Edit</Th>
                                                <Th>Nomor RKH</Th>
                                                <Th>Tanggal RKH</Th>
                                                <Th>Stage</Th>
                                                <Th>Estate</Th>
                                                <Th>Divisi</Th>
                                                <Th>Terakhir Sync</Th>
                                                <Th textAlign="center">Detail</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {data.map((rkh) => (
                                                <Tr key={rkh.id} _hover={{ bg: 'gray.50' }}>
                                                    <Td textAlign="center">
                                                        <IconButton
                                                            aria-label="Edit lokasi"
                                                            icon={<FiEdit />}
                                                            size="sm"
                                                            colorScheme="green"
                                                            variant="ghost"
                                                            onClick={() => handleEditLocation(rkh.id, rkh.rkh_nbr)}
                                                        />
                                                    </Td>
                                                    <Td fontWeight="bold" color="blue.600">
                                                        {rkh.rkh_nbr}
                                                    </Td>
                                                    <Td>{formatDate(rkh.rkh_date)}</Td>
                                                    <Td>
                                                        <Badge colorScheme={RKH_STAGE_COLORS[rkh.stage]}>
                                                            {RKH_STAGE_LABELS[rkh.stage]}
                                                        </Badge>
                                                    </Td>
                                                    <Td>{rkh.estate_name}</Td>
                                                    <Td>{rkh.division_name}</Td>
                                                    <Td fontSize="sm">{rkh.date_sync ? formatDate(rkh.date_sync) : '-'}</Td>
                                                    <Td textAlign="center">
                                                        <IconButton
                                                            aria-label="Lihat detail"
                                                            icon={<FiEye />}
                                                            size="sm"
                                                            colorScheme="blue"
                                                            variant="ghost"
                                                            onClick={() => handleViewDetail(rkh.id)}
                                                        />
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </Box>
                            ) : (
                                /* Mobile Card View */
                                <VStack spacing={4}>
                                    {data.map((rkh) => (
                                        <Card key={rkh.id} w="100%" boxShadow="sm">
                                            <CardBody>
                                                <Stack spacing={3}>
                                                    <Flex justify="space-between" align="start">
                                                        <Box>
                                                            <Text fontSize="xs" color="gray.600">
                                                                Nomor RKH
                                                            </Text>
                                                            <Text fontWeight="bold" color="blue.600">
                                                                {rkh.rkh_nbr}
                                                            </Text>
                                                        </Box>
                                                        <Badge colorScheme={RKH_STAGE_COLORS[rkh.stage]}>
                                                            {RKH_STAGE_LABELS[rkh.stage]}
                                                        </Badge>
                                                    </Flex>

                                                    <Divider />

                                                    <Flex justify="space-between">
                                                        <Box>
                                                            <Text fontSize="xs" color="gray.600">Tanggal</Text>
                                                            <Text fontSize="sm" fontWeight="medium">
                                                                {formatDate(rkh.rkh_date)}
                                                            </Text>
                                                        </Box>
                                                        <Box textAlign="right">
                                                            <Text fontSize="xs" color="gray.600">Estate / Divisi</Text>
                                                            <Text fontSize="sm" fontWeight="medium">
                                                                {rkh.estate_name} / {rkh.division_name}
                                                            </Text>
                                                        </Box>
                                                    </Flex>

                                                    <HStack spacing={2}>
                                                        <Button
                                                            leftIcon={<FiEdit />}
                                                            colorScheme="green"
                                                            size="sm"
                                                            flex={1}
                                                            onClick={() => handleEditLocation(rkh.id, rkh.rkh_nbr)}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            leftIcon={<FiEye />}
                                                            colorScheme="blue"
                                                            size="sm"
                                                            flex={1}
                                                            onClick={() => handleViewDetail(rkh.id)}
                                                        >
                                                            Lihat Detail
                                                        </Button>
                                                    </HStack>
                                                </Stack>
                                            </CardBody>
                                        </Card>
                                    ))}
                                </VStack>
                            )}

                            {/* Pagination */}
                            {pagination && pagination.total_pages > 1 && (
                                <Flex justify="center" align="center" gap={4} mt={6}>
                                    <IconButton
                                        aria-label="Previous page"
                                        icon={<FiChevronLeft />}
                                        onClick={() => handlePageChange(pagination.page - 1)}
                                        isDisabled={pagination.page === 1}
                                        size="sm"
                                    />
                                    <HStack spacing={2}>
                                        <Text fontSize="sm" color="gray.600">
                                            Halaman {pagination.page} dari {pagination.total_pages}
                                        </Text>
                                    </HStack>
                                    <IconButton
                                        aria-label="Next page"
                                        icon={<FiChevronRight />}
                                        onClick={() => handlePageChange(pagination.page + 1)}
                                        isDisabled={pagination.page === pagination.total_pages}
                                        size="sm"
                                    />
                                </Flex>
                            )}
                        </VStack>
                    )}
                </Box>
            </Flex>

            {/* Detail Modal */}
            <RKHDetailModal
                isOpen={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                data={detailData}
                loading={detailLoading}
                error={detailError}
            />

            {/* Location Modal */}
            {selectedRKH && user && (
                <RKHLocationModal
                    isOpen={isLocationModalOpen}
                    onClose={handleCloseLocationModal}
                    rkhId={selectedRKH.id}
                    rkhNumber={selectedRKH.number}
                    userEmail={user.email}
                    onSuccess={handleLocationUpdateSuccess}
                />
            )}
        </Box>
    );
};

export default RKHPage;
